import { useEffect, useState } from "react";
import { ArrowLeft, Copy, QrCode, Share2, Check } from "lucide-react";
import { FaWhatsapp, FaFacebookF } from "react-icons/fa6";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import {
  useShareMe,
  useClaimShareReward,
  copyToClipboard,
} from "@/lib/share";

const WA_TEMPLATE = (url) =>
  `I'm almost done with my 2026 World Cup album! 🏆 Check out my missing and duplicate stickers on FiguMatch and let's trade locally. ${url}`;

const nativeShareSupported = () =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

export default function ShareOptionsModal({ open, onOpenChange }) {
  useOverlayLock(open);
  const { data: shareMe } = useShareMe(open);
  const claim = useClaimShareReward();

  const [view, setView] = useState("channels"); // "channels" | "qr"
  const [copied, setCopied] = useState(false);
  const [hasNative] = useState(() => nativeShareSupported());

  // Reset to channel view when the modal closes
  useEffect(() => {
    if (!open) {
      setView("channels");
      setCopied(false);
    }
  }, [open]);

  const shareUrl = shareMe?.share_url || "https://appfigumatch.xyz";
  const alreadyClaimed = shareMe?.aha_share_claimed === true;
  const subtitle = alreadyClaimed
    ? "You've already earned your starter FiguTokens — keep spreading the word!"
    : "Earn +10 FiguTokens on your first share";

  async function handleShareIntent() {
    if (shareMe?.aha_share_claimed) return;
    try {
      const res = await claim.mutateAsync();
      if (res?.newly_claimed) {
        toast.success("Thanks for sharing! +10 FiguTokens claimed.");
      }
    } catch {
      /* silent — share UX continues regardless */
    }
  }

  function shareWhatsapp() {
    const text = WA_TEMPLATE(shareUrl);
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer"
    );
    handleShareIntent();
  }

  function shareFacebook() {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`;
    window.open(url, "_blank", "popup=yes,width=600,height=500,noopener,noreferrer");
    handleShareIntent();
  }

  async function shareNative() {
    try {
      await navigator.share({
        title: "FiguMatch",
        text: "Trade stickers with collectors near you",
        url: shareUrl,
      });
      await handleShareIntent();
    } catch (err) {
      if (err && err.name !== "AbortError") {
        console.error("native share failed", err);
      }
    }
  }

  async function copyLink() {
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success("Link copied");
    else toast.error("Couldn't copy. Long-press the link manually.");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    await handleShareIntent();
  }

  function showQrCode() {
    setView("qr");
    handleShareIntent();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-options-modal"
        className={
          "bg-[#0A0A0A] border border-white/10 text-white p-0 overflow-hidden " +
          // Mobile: bottom sheet ; Desktop: centered card
          "fixed left-0 right-0 bottom-0 top-auto translate-x-0 translate-y-0 " +
          "rounded-t-2xl rounded-b-none max-w-none w-full max-h-[90vh] " +
          "sm:left-[50%] sm:right-auto sm:top-[50%] sm:bottom-auto " +
          "sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:max-w-[440px] sm:w-[440px] sm:max-h-[90vh] " +
          "shadow-[0_-30px_60px_-30px_rgba(229,184,58,0.4)] sm:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]"
        }
      >
        {/* Mobile grabber */}
        <div className="sm:hidden flex justify-center pt-2.5">
          <span className="h-1 w-10 bg-white/15 rounded-full" />
        </div>

        {view === "channels" ? (
          <div className="px-5 sm:px-6 pt-3 sm:pt-6 pb-5 sm:pb-6 overflow-y-auto">
            <DialogTitle className="font-display text-xl sm:text-2xl font-bold text-[#E5B83A]">
              Share FiguMatch
            </DialogTitle>
            <DialogDescription
              data-testid="share-options-subtitle"
              className="font-body text-xs sm:text-sm text-[#A1A1AA] mt-1.5"
            >
              {subtitle}
            </DialogDescription>

            <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
              <ChannelButton
                testid="share-channel-whatsapp"
                icon={<FaWhatsapp className="h-5 w-5" style={{ color: "#25D366" }} />}
                label="Share via WhatsApp"
                onClick={shareWhatsapp}
              />
              <ChannelButton
                testid="share-channel-qr"
                icon={<QrCode className="h-5 w-5 text-[#E5B83A]" />}
                label="Show QR Code"
                onClick={showQrCode}
              />
              <ChannelButton
                testid="share-channel-facebook"
                icon={<FaFacebookF className="h-5 w-5" style={{ color: "#1877F2" }} />}
                label="Share on Facebook"
                onClick={shareFacebook}
              />
              {hasNative ? (
                <ChannelButton
                  testid="share-channel-native"
                  icon={<Share2 className="h-5 w-5 text-[#E5B83A]" />}
                  label="More options..."
                  onClick={shareNative}
                />
              ) : null}
              <ChannelButton
                testid="share-channel-copy"
                icon={
                  copied ? (
                    <Check className="h-5 w-5 text-[#E5B83A]" />
                  ) : (
                    <Copy className="h-5 w-5 text-[#E5B83A]" />
                  )
                }
                label={copied ? "Copied ✓" : "Copy Link"}
                onClick={copyLink}
                fullSpan
              />
            </div>

            <p
              data-testid="share-options-footer"
              className="mt-5 text-[10px] font-body text-[#A1A1AA]/60 text-center"
            >
              Sharing link:{" "}
              <span className="font-mono text-[#E5B83A] break-all">{shareUrl}</span>
            </p>
          </div>
        ) : (
          <div className="px-5 sm:px-6 pt-3 sm:pt-6 pb-6 overflow-y-auto">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setView("channels")}
                aria-label="Back to share options"
                data-testid="share-qr-back"
                className="h-8 w-8 rounded-md flex items-center justify-center text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <DialogTitle className="font-display text-lg sm:text-xl font-bold text-[#E5B83A] m-0">
                Scan to share
              </DialogTitle>
            </div>

            <div className="mt-5 flex flex-col items-center gap-4">
              <div
                data-testid="share-qr-svg-wrap"
                className="rounded-xl bg-[#0a0a0a] border border-[#E5B83A]/20 p-4"
              >
                <QRCodeSVG
                  value={shareUrl}
                  size={240}
                  bgColor="#0a0a0a"
                  fgColor="#E5B83A"
                  level="M"
                  includeMargin
                />
              </div>
              <p className="font-body text-sm text-white">Scan to visit my profile</p>
              <p className="font-mono text-xs text-[#E5B83A] break-all text-center">
                {shareUrl}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChannelButton({ icon, label, onClick, testid, fullSpan }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        "h-14 px-4 rounded-lg bg-[#1a1a1a] hover:bg-[#222] border border-white/5 hover:border-[#E5B83A]/40 " +
        "flex items-center gap-3 text-left font-body text-sm text-white transition-all " +
        (fullSpan ? "sm:col-span-2 " : "")
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
    </button>
  );
}
